import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';

interface UnsubscribePage {
  items: Array<{
    contactId: string;
    name: string;
    email: string;
    unsubscribedAt: string | null;
    attribution: 'recorded' | 'matched_click';
  }>;
  total: number;
  page: number;
  pageSize: number;
}

export function CampaignUnsubscribes({ campaignId, count }: { campaignId: string; count: number }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const request = useRef<AbortController | null>(null);
  const [result, setResult] = useState<UnsubscribePage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => () => request.current?.abort(), [campaignId]);

  async function load(nextPage: number) {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setPage(nextPage);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await api<UnsubscribePage>(
        `/campaigns/${campaignId}/unsubscribes?page=${nextPage}`,
        {
          signal: controller.signal,
          cache: 'no-store',
        },
      );
      if (!controller.signal.aborted) setResult(response);
    } catch (err) {
      if (!controller.signal.aborted)
        setError(errMsg(err, 'Could not load unsubscribed recipients.'));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="min-w-0 cursor-pointer rounded-md border p-2 text-left hover:bg-muted/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        aria-label={`Unsubscribed: ${count}. View recipients`}
        aria-haspopup="dialog"
        onClick={() => {
          dialog.current?.showModal();
          void load(1);
        }}
      >
        <div className="text-xs text-muted-foreground">Unsubscribed</div>
        <div className="text-lg underline decoration-dotted underline-offset-4">{count}</div>
      </button>
      <dialog
        ref={dialog}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="m-auto max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-xl border bg-background p-5 text-foreground shadow-lg backdrop:bg-black/40"
        onClose={() => request.current?.abort()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-xl">
            Unsubscribed recipients
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="cursor-pointer"
            onClick={() => dialog.current?.close()}
          >
            Close
          </Button>
        </div>
        <p id={descriptionId} className="mt-2 text-sm text-muted-foreground">
          Confirmed unsubscribes for this campaign. Times shown in {timezone}.
        </p>
        <div className="mt-4" aria-busy={loading}>
          {loading && (
            <p role="status" className="text-sm">
              Loading recipients…
            </p>
          )}
          {error && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
              <Button
                type="button"
                variant="outline"
                className="cursor-pointer"
                onClick={() => void load(page)}
              >
                Retry
              </Button>
            </div>
          )}
          {result && (
            <>
              <p className="mb-3 text-sm">
                {result.total} unsubscribed {result.total === 1 ? 'recipient' : 'recipients'}
              </p>
              {result.total === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No confirmed unsubscribes for this campaign.
                </p>
              ) : (
                <table
                  className="data-table w-full table-fixed"
                  aria-label="Unsubscribed recipients"
                >
                  <thead>
                    <tr>
                      <th scope="col">Recipient</th>
                      <th scope="col">Unsubscribed at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.items.map((recipient) => (
                      <tr key={recipient.contactId}>
                        <td className="[overflow-wrap:anywhere]">
                          {recipient.name && <div>{recipient.name}</div>}
                          <div className="text-xs text-muted-foreground">{recipient.email}</div>
                        </td>
                        <td className="text-xs">
                          {recipient.unsubscribedAt ? (
                            <time dateTime={recipient.unsubscribedAt}>
                              {fmtDateTime(recipient.unsubscribedAt)}
                            </time>
                          ) : (
                            'Time unavailable'
                          )}
                          {recipient.attribution === 'matched_click' && (
                            <div className="mt-1 text-muted-foreground">
                              Matched from link activity
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {result.total > result.pageSize && (
                <div className="mt-4 flex items-center justify-between gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer"
                    disabled={page <= 1}
                    onClick={() => void load(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-xs">
                    Page {page} of {Math.ceil(result.total / result.pageSize)}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer"
                    disabled={page * result.pageSize >= result.total}
                    onClick={() => void load(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </dialog>
    </>
  );
}
